import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { restoreCsrTheme } from '../../csr/csr-theme';

const API_URL = environment.apiHost;

interface DirectoryConfigMatrix {
  [viewerRole: string]: string[];   // e.g. { CSR: ['INTERNAL_USER','SUPERVISOR'], ... }
}

interface RowState {
  viewerRole: string;
  viewerLabel: string;
  pools: { role: string; label: string; checked: boolean }[];
  saving: boolean;
  saved: boolean;
  error: string;
}

const ALL_POOLS: { role: string; label: string }[] = [
  { role: 'CSR',           label: 'CSR Officer Pool' },
  { role: 'INTERNAL_USER', label: 'Internal User Pool' },
  { role: 'SUPERVISOR',    label: 'Supervisor Pool' },
  { role: 'ADMIN',         label: 'Admin Pool' },
  { role: 'MANAGER',       label: 'Manager Pool' },
];

const VIEWER_ROLES: { role: string; label: string }[] = [
  { role: 'CSR',           label: 'CSR Officers' },
  { role: 'INTERNAL_USER', label: 'Internal Users' },
  { role: 'MANAGER',       label: 'Managers' },
  { role: 'SUPERVISOR',    label: 'Supervisors' },
  { role: 'ADMIN',         label: 'Admins' },
  { role: 'SUPER_ADMIN',   label: 'Super Admins' },
];

@Component({
  selector: 'app-directory-config',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './directory-config.component.html',
  styleUrls: ['./directory-config.component.scss'],
})
export class DirectoryConfigComponent implements OnInit {

  rows: RowState[] = [];
  loading = true;
  loadError = '';

  readonly allPools = ALL_POOLS;

  ngOnInit(): void {
    restoreCsrTheme();
    this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.loadError = '';
    try {
      const token = localStorage.getItem('auth-token') ?? '';
      const res = await fetch(`${API_URL}/api/v1/telephony/extensions/directory-config`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) { this.loadError = `Failed to load config (HTTP ${res.status})`; return; }
      const json: any = await res.json();
      const matrix: DirectoryConfigMatrix = json?.data ?? json ?? {};
      this.rows = VIEWER_ROLES.map(vr => ({
        viewerRole:  vr.role,
        viewerLabel: vr.label,
        pools: ALL_POOLS.map(p => ({
          role:    p.role,
          label:   p.label,
          checked: (matrix[vr.role] ?? []).includes(p.role),
        })),
        saving: false,
        saved:  false,
        error:  '',
      }));
    } catch (e: any) {
      this.loadError = e?.message ?? 'Network error';
    } finally {
      this.loading = false;
    }
  }

  async save(row: RowState): Promise<void> {
    row.saving = true;
    row.saved  = false;
    row.error  = '';
    const visiblePools = row.pools.filter(p => p.checked).map(p => p.role);
    try {
      const token = localStorage.getItem('auth-token') ?? '';
      const res = await fetch(
        `${API_URL}/api/v1/telephony/extensions/directory-config/${row.viewerRole}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ visiblePools }),
        }
      );
      if (res.ok) {
        row.saved = true;
        setTimeout(() => { row.saved = false; }, 2500);
      } else {
        const err: any = await res.json().catch(() => ({}));
        row.error = err?.message ?? `HTTP ${res.status}`;
      }
    } catch (e: any) {
      row.error = e?.message ?? 'Network error';
    } finally {
      row.saving = false;
    }
  }
}
